import { useMemo } from 'react';

// Styles
import cornerStyles from '../../../styles/tableCardCorners.module.css';
import styles from './TableGroupFooter.module.css';

// Utils
import cn from '../../../utils/cn';

// Domain
import { buildTableHeadRows } from '../../../domain/table/buildTableHeadRows';
import {
  buildLeafColumnIdIndexMap,
  footerStatHeaderOverlapsHoveredLeafIndicesFromMap,
} from '../../../domain/table/tableColumnHoverUseCase';
import { useTableColumnHoverStateOptional } from '../../../domain/table/useTableColumnHover';
import { getCommonPinningStyles } from '../../../domain/table/tableUseCase';
import {
  getPinnedAwareVisibleLeafColumns,
  getThSpanWidthPx,
} from '../../../domain/table/tableHeadUseCase';

/**
 * Per-group stats footer (one row aligned with table headers).
 * Per-group stats row at the bottom of each table group (aligned with leaf columns, API text-align like TableHead stat line).
 * @param {object} params
 * @param {TableInstance} [params.table] - Table instance
 * @param {TableGroupStatsValue} [params.groupStatsValue] - Group stats indexed by columnId
 * @returns {JSX.Element | null}
 */
function TableGroupFooter({
  table,
  groupStatsValue,
}) {
  const { hoveredLeafIndices } = useTableColumnHoverStateOptional();

  const { columnVisibility, columnOrder, columnPinning } = table.getState();

  const hoverLeafLookup = useMemo(() => {
    const leaves = getPinnedAwareVisibleLeafColumns(table);
    return {
      leafCount: leaves.length,
      columnIdToIndex: buildLeafColumnIdIndexMap(leaves),
    };
  }, [table, columnVisibility, columnOrder, columnPinning]);

  const columnIdToStatAlign = useMemo(() => {
    const leaves = getPinnedAwareVisibleLeafColumns(table);
    const headRows = buildTableHeadRows(leaves);
    const lastRow = headRows[headRows.length - 1];
    if (!lastRow) {
      return new Map();
    }
    return new Map(
      lastRow.cells.map((c) => [c.columnId, c.header.align ?? 'left']),
    );
  }, [table, columnVisibility, columnOrder, columnPinning]);

  const justifyFromAlign = (align) => {
    if (align === 'right') return 'flex-end';
    if (align === 'center') return 'center';
    return 'flex-start';
  };

  const headerGroups = table.getHeaderGroups();
  const leafHeaderGroup = headerGroups[headerGroups.length - 1];
  if (!leafHeaderGroup) {
    return null;
  }

  let leafStartIndex = 0;

  return (
    <tfoot
      className={styles['table-group-footer']}
    >
      <tr className={styles['table-group-footer-row']} key={leafHeaderGroup.id}>
        {/** @type {TableHeader[]} */ (leafHeaderGroup.headers).map((header) => {
          const isColumnCheckbox = header.column.columnDef?.type === 'static-checkbox';
          const colSpan = isColumnCheckbox ? 1 : header.colSpan;
          const isFirstLeaf = leafStartIndex === 0;
          const isLastLeaf = leafStartIndex + colSpan === hoverLeafLookup.leafCount;
          leafStartIndex += colSpan;
          const pinStyles = getCommonPinningStyles(header.column);
          const widthPx = getThSpanWidthPx(table, header.column.id, colSpan);
          const mergedStyle = {
            ...pinStyles,
            width: widthPx,
            maxWidth: widthPx,
          };
          const isColumnHovered = footerStatHeaderOverlapsHoveredLeafIndicesFromMap(
            hoverLeafLookup.leafCount,
            hoverLeafLookup.columnIdToIndex,
            header,
            hoveredLeafIndices,
          );
          const hasStatValue = groupStatsValue?.[header.column.id]?.statValue != null;
          const align = columnIdToStatAlign.get(header.column.id) ?? 'left';
          const statRaw = groupStatsValue?.[header.column.id]?.statValue;
          const statText = statRaw != null && String(statRaw).trim() !== ''
            ? String(statRaw).trim()
            : '';
          const statDisplay = isColumnCheckbox ? '\u00a0' : (statText || '\u00a0');
          return (
            <th
              className={cn([
                styles['table-group-footer-cell'],
                header.column.getIsPinned() && styles['table-group-footer-cell-pinned'],
                isColumnCheckbox && styles['table-group-footer-cell-checkbox'],
                isColumnHovered && hasStatValue && styles['table-group-footer-cell-column-hover'],
                isFirstLeaf && cornerStyles['corner-bl'],
                isLastLeaf && cornerStyles['corner-br'],
              ])}
              key={header.id}
              colSpan={colSpan}
              style={mergedStyle}
            >
              <div
                className={cn([
                  styles['table-group-footer-cell-button'],
                  header.column.getCanSort() ? styles['table-group-footer-cell-button-sortable'] : '',
                ])}
              >
                <div
                  className={styles['table-group-footer-cell-content']}
                  style={{
                    textAlign: align,
                    justifyContent: justifyFromAlign(align),
                  }}
                >
                  {statDisplay}
                </div>
              </div>
            </th>
          );
        })}
      </tr>
    </tfoot>
  );
}

export default TableGroupFooter;
